import { useTranslation } from "react-i18next";
import { FormRadioButtons } from "../../../common";
import { MultipleChoiceOptionStringId } from "../../../../types/forms/MultipleChoiceOptions";
import {
  getSelectedDeclineOption,
  shouldShowDeclineChoiceNote,
} from "./feedbackDeclineOptions";

interface FeedbackDeclineStepBodyProps {
  declineOptions: MultipleChoiceOptionStringId[];
  onChange: (options: MultipleChoiceOptionStringId[]) => void;
  saveError: string | null;
  uniqueFieldName?: string;
}

export function FeedbackDeclineStepBody({
  declineOptions,
  onChange,
  saveError,
  uniqueFieldName = "feedback-decline-reason",
}: FeedbackDeclineStepBodyProps) {
  const { t } = useTranslation();
  const selectedOption = getSelectedDeclineOption(declineOptions);

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 leading-relaxed">
        {t("Feedback.Decline.DeclinePrompt", {
          defaultValue: "Why don't you want to give feedback?",
        })}
      </p>
      <div className="p-1">
        <FormRadioButtons
          uniqueFieldName={uniqueFieldName}
          values={declineOptions}
          onChange={onChange}
          layout="VERTICAL"
          showSelectedCheckIcon
          containerClassNames="space-y-2"
        />
      </div>
      {shouldShowDeclineChoiceNote(selectedOption) && (
        <p className="text-sm text-gray-600 leading-relaxed">
          {t("Feedback.Decline.DeclineChoiceNote", {
            defaultValue:
              "The choice you've made above will be noted against this feedback request",
          })}
        </p>
      )}
      {saveError && <p className="text-xs text-red-600">{saveError}</p>}
    </div>
  );
}

interface FeedbackDeclineStepFooterProps {
  declineOptions: MultipleChoiceOptionStringId[];
  onBack: () => void;
  onSubmit: () => void;
  isSaving: boolean;
}

export function FeedbackDeclineStepFooter({
  declineOptions,
  onBack,
  onSubmit,
  isSaving,
}: FeedbackDeclineStepFooterProps) {
  const { t } = useTranslation();
  const hasSelection = getSelectedDeclineOption(declineOptions) != null;

  return (
    <div className="text-right">
      <button
        type="button"
        className="inline-block btn-secondary m-2"
        onClick={onBack}
        disabled={isSaving}
      >
        {t("Common.Back", { defaultValue: "Back" })}
      </button>
      <button
        type="button"
        className="inline-block btn-primary m-2"
        onClick={onSubmit}
        disabled={!hasSelection || isSaving}
      >
        {isSaving
          ? t("Common.Submitting", { defaultValue: "Submitting…" })
          : t("Common.Submit", { defaultValue: "Submit" })}
      </button>
    </div>
  );
}

interface FeedbackDeclineStepProps extends FeedbackDeclineStepBodyProps {
  onBack: () => void;
  onSubmit: () => void;
  isSaving: boolean;
}

/** Body + footer together (e.g. when not using ModalPopup footerComponent). */
function FeedbackDeclineStep({
  onBack,
  onSubmit,
  isSaving,
  ...bodyProps
}: FeedbackDeclineStepProps) {
  return (
    <>
      <FeedbackDeclineStepBody {...bodyProps} />
      <div className="text-right mt-4 pt-4 border-t border-gray-100">
        <FeedbackDeclineStepFooter
          declineOptions={bodyProps.declineOptions}
          onBack={onBack}
          onSubmit={onSubmit}
          isSaving={isSaving}
        />
      </div>
    </>
  );
}

export default FeedbackDeclineStep;
